import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ErrorModal } from '../ErrorModal';
import { CollectionFormModal } from '../CollectionFormModal';
import { CollectionActionsSheet } from '../CollectionActionsSheet';
import { CollectionActionsDropdown } from '../CollectionActionsDropdown';
import * as S from './CollectionCard.styled';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Portal } from '@/components/UI/Portal/Portal';
import {
  BookmarkCollection,
  DESTROY_COLLECTION,
} from '@/features/BookmarkCollections/api';
import { useToastStore } from '@/store/useToastStore';
import { useIosNudgeScrollOffTop } from '@/hooks/useIosNudgeScrollOffTop';

interface CollectionCardProps {
  collection: BookmarkCollection;
  onUpdate: () => void;
  onActionSuccess: (message: string) => void;
}

export const CollectionCard = ({
  collection,
  onUpdate,
  onActionSuccess,
}: CollectionCardProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const [destroy] = useMutation(DESTROY_COLLECTION);
  const closeToast = useToastStore(state => state.closeToast);
  const iosNudgeScrollOffTop = useIosNudgeScrollOffTop();

  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditDropdownOpen, setIsEditDropdownOpen] = useState(false);

  const mainImage = collection.photos[0]?.url;
  const hasImage = Boolean(mainImage);

  const smallImages = useMemo(() => {
    return Array.from({ length: 3 }).map(
      (_, index) => collection.photos[index + 1]?.url
    );
  }, [collection.photos]);

  useEffect(() => {
    if (isDesktop) {
      setIsEditSheetOpen(false);
    } else {
      setIsEditModalOpen(false);
      setIsEditDropdownOpen(false);
    }
  }, [isDesktop]);

  const handleDeleteCollection = async () => {
    if (!collection) return;

    const { id, name } = collection;

    try {
      await destroy({ variables: { bookmarkCollectionId: id } });

      onUpdate();
      onActionSuccess(
        t('bookmark_collections.delete_collection_success', { name })
      );
    } catch (error) {
      setIsErrorOpen(true);
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setIsEditSheetOpen(false);
    onUpdate();
  };

  const handleEditCollection = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    closeToast();

    if (isDesktop) {
      setIsEditDropdownOpen(true);
      return;
    }

    iosNudgeScrollOffTop();
    setIsEditSheetOpen(true);
  };

  const renderSmallImages = () => {
    return smallImages.map((img: string, index) => {
      const isLast = index === 2;
      const isFirst = index === 0;

      return img ? (
        <S.SmallImage
          key={index}
          src={img}
          $isLast={isLast}
          $isFirst={isFirst}
        />
      ) : (
        <S.SmallImagePlaceholder
          key={index}
          $isLast={isLast}
          $isFirst={isFirst}
        />
      );
    });
  };

  return (
    <>
      <S.Card
        href={`/collections/${collection.id}`}
        $isInteractionBlocked={isEditDropdownOpen}
      >
        <S.ImagesContainer $isPlaceholder={!hasImage}>
          <S.CollectionEditButton
            onClick={handleEditCollection}
            $isActive={isEditDropdownOpen}
            data-testid="collections-grid-item-edit"
          >
            {isDesktop && (
              <CollectionActionsDropdown
                hasSharing
                hasSelection={false}
                isOpen={isEditDropdownOpen}
                collection={collection}
                onDelete={handleDeleteCollection}
                onEdit={() => setIsEditModalOpen(true)}
                onClose={() => setIsEditDropdownOpen(false)}
              />
            )}
          </S.CollectionEditButton>

          {hasImage ? (
            <>
              <S.MainImage src={mainImage} />
              {renderSmallImages()}
            </>
          ) : (
            <S.MainImagePlaceholder />
          )}
        </S.ImagesContainer>

        <S.Title data-testid="collections-grid-item-title">
          {collection.name}
        </S.Title>
        <S.Count>
          {t('bookmark_collections.item_ad_count', {
            count: collection.ads.totalCount,
          })}
        </S.Count>
      </S.Card>

      <Portal>
        <CollectionActionsSheet
          hasSharing
          hasSelection={false}
          collection={collection}
          isOpen={isEditSheetOpen}
          onDelete={handleDeleteCollection}
          onClose={() => setIsEditSheetOpen(false)}
        />
      </Portal>

      <Portal>
        <CollectionFormModal
          mode="edit"
          collection={collection}
          isOpen={isEditModalOpen}
          onSuccess={handleEditSuccess}
          onClose={() => setIsEditModalOpen(false)}
        />
      </Portal>

      <Portal>
        <ErrorModal
          isOpen={isErrorOpen}
          onClose={() => setIsErrorOpen(false)}
          message={t('bookmark_collections.universal_error_message')}
        />
      </Portal>
    </>
  );
};
